import { styled, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { Typography } from '../Typography/Typography';
import { WorkflowStage } from '../../types';

export const STAGE_LABEL: Record<WorkflowStage, string> = {
  [WorkflowStage.ACCOUNTANT]: 'Accountant',
  [WorkflowStage.MANAGER]: 'Manager',
  [WorkflowStage.DEAL_OWNER]: 'Deal Owner',
  [WorkflowStage.SVP_MANAGER]: 'SVP Manager',
};

// The two implicit endpoints of every pipeline (not approval stages). Overridable via stageLabels too.
export const SUBMITTER_TOKEN = 'SUBMITTER';
export const CLOSED_TOKEN = 'CLOSED';
const ENDPOINT_LABEL: Record<string, string> = {
  [SUBMITTER_TOKEN]: 'Submitter',
  [CLOSED_TOKEN]: 'Closed',
};

/** Resolve a node's display label: per-workflow override (if a non-empty string) else the default. */
function resolveLabel(token: string, defaultLabel: string, overrides?: Record<string, string> | null): string {
  const override = overrides?.[token];
  return typeof override === 'string' && override.trim() ? override : defaultLabel;
}

type StageNode = { key: string; label: string; kind: 'endpoint' | 'stage' };

// Container staggers each row in, left-to-right; each row (chevron + pill) springs up together.
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.03 } },
};
const rowVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 480, damping: 30 } },
};

type Props = {
  /** Ordered approval stages the timesheet walks through. Empty = no approval (Submitter -> Closed). */
  stages: WorkflowStage[];
  /**
   * Optional value that, when changed, remounts the strip so the stagger animation replays.
   * The user form passes the selected workflow id; a static page can omit it.
   */
  animationKey?: string | number;
  /** `compact` for the user form (default), `large` for the standalone Workflows page. */
  size?: 'compact' | 'large';
  /**
   * Optional per-workflow display-label overrides (Workflow.stageLabels), keyed by node token
   * (SUBMITTER, ACCOUNTANT, MANAGER, DEAL_OWNER, SVP_MANAGER, CLOSED). Missing keys use the default.
   */
  labels?: Record<string, string> | null;
};

/**
 * Presentational approval-pipeline strip: `Submitter -> <stages, in order> -> Closed`.
 * Endpoints are muted, approval stages accented, so the pipeline reads at a glance. Animates in with a
 * left-to-right stagger. Shared by the user-form workflow indicator and the read-only Workflows page.
 */
export function WorkflowStagesStrip({ stages, animationKey, size = 'compact', labels }: Props) {
  const { colors } = useTheme();
  const large = size === 'large';

  const nodes = useMemo<StageNode[]>(() => {
    const middle: StageNode[] = stages.map((stage) => ({
      key: stage,
      label: resolveLabel(stage, STAGE_LABEL[stage] ?? stage, labels),
      kind: 'stage',
    }));
    return [
      { key: 'submitter', label: resolveLabel(SUBMITTER_TOKEN, ENDPOINT_LABEL[SUBMITTER_TOKEN], labels), kind: 'endpoint' },
      ...middle,
      { key: 'closed', label: resolveLabel(CLOSED_TOKEN, ENDPOINT_LABEL[CLOSED_TOKEN], labels), kind: 'endpoint' },
    ];
  }, [stages, labels]);

  return (
    <Wrap
      key={animationKey}
      $large={large}
      variants={containerVariants}
      initial='hidden'
      animate='visible'
      aria-label={`Approval pipeline: ${nodes.map((node) => node.label).join(' then ')}`}
    >
      {nodes.map((node, index) => {
        const isStage = node.kind === 'stage';
        return (
          <Row key={node.key} $large={large} variants={rowVariants}>
            {index > 0 && (
              <Chevron $large={large} viewBox='0 0 24 24' style={{ color: colors.icon.defaultControls }}>
                <path
                  d='M9 6l6 6-6 6'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </Chevron>
            )}
            <Pill
              $large={large}
              style={{
                backgroundColor: isStage ? colors.surfaceInteraction.pressed : colors.surfaceStatus.noAction,
              }}
            >
              <Typography
                variant={large ? 'body' : 'caption'}
                color={isStage ? colors.text.main : colors.text.secondary}
              >
                {node.label}
              </Typography>
            </Pill>
          </Row>
        );
      })}
    </Wrap>
  );
}

const Wrap = styled(motion.div)<{ $large: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ $large }) => ($large ? '8px' : '6px')};
`;

const Row = styled(motion.div)<{ $large: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $large }) => ($large ? '8px' : '6px')};
`;

const Chevron = styled('svg')<{ $large: boolean }>`
  width: ${({ $large }) => ($large ? '20px' : '16px')};
  height: ${({ $large }) => ($large ? '20px' : '16px')};
  flex-shrink: 0;
`;

const Pill = styled('div')<{ $large: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ $large }) => ($large ? '8px 16px' : '4px 10px')};
  border-radius: ${({ $large }) => ($large ? '20px' : '14px')};
  white-space: nowrap;
`;
